import api from '@/lib/api';
import type { RunnerCacheUsage } from '@/api/caches';

/** A runner using a connection; `recreate` is only set in `METRICS_CONNECTION_RECREATE_REQUIRED`. */
export type MetricsRunner = RunnerCacheUsage;

/** Header values are write-only and never returned. */
export interface MetricsConnection {
  id: string;
  name: string;
  /** OTLP base endpoint; the collector appends `/v1/metrics`. */
  endpoint: string;
  headers: { name: string }[];
  created: string;
  updated: string;
  /** Runners in any state that use this connection, which a change of it affects. */
  usedBy: MetricsRunner[];
  /** External runners whose installed agents use it; missing from backends that predate them. */
  externalRunners?: ExternalMetricsRunner[];
}

/** Never recreated: a change needs its reinstall, a delete stops its agent's sending. */
export interface ExternalMetricsRunner {
  runnerId: string;
  name: string;
  monitoringEnabled: boolean;
}

export interface MetricsConnectionRequest {
  name: string;
  endpoint: string;
  /** On update, a header without `value` keeps the stored value of the header with this name. */
  headers: { name: string; value?: string }[];
}

export interface MetricsConnectionUpdateResponse {
  connection: MetricsConnection;
  recreatedRunners: MetricsRunner[];
  /** External runners that keep the previous settings until their next install command. */
  reinstallRunners?: ExternalMetricsRunner[];
}

export interface MetricsConnectionDeleteResponse {
  /** Runners whose Metrics was switched off. */
  detachedRunners: MetricsRunner[];
  recreatedRunners: MetricsRunner[];
  /** External runners whose agents stopped sending until reinstalled. */
  stoppedExternalRunners?: ExternalMetricsRunner[];
}

export async function listMetricsConnections() {
  const { data } = await api.get<MetricsConnection[]>('metrics-connections');
  return data;
}

export async function getMetricsConnection(id: string) {
  const { data } = await api.get<MetricsConnection>(`metrics-connections/${id}`);
  return data;
}

export async function createMetricsConnection(payload: MetricsConnectionRequest) {
  const { data } = await api.post<MetricsConnection>('metrics-connections', payload);
  return data;
}

/**
 * Rejects with HTTP 409 `METRICS_CONNECTION_RECREATE_REQUIRED` and the affected `runners` when active or unknown
 * runners use the connection and [recreateRunners] is not set.
 */
export async function updateMetricsConnection(
  id: string,
  payload: MetricsConnectionRequest,
  recreateRunners = false
) {
  const { data } = await api.put<MetricsConnectionUpdateResponse>(
    `metrics-connections/${id}`,
    payload,
    {
      params: recreateRunners ? { recreateRunners: true } : undefined,
    }
  );
  return data;
}

/** Rejects like [updateMetricsConnection]. */
export async function deleteMetricsConnection(id: string, recreateRunners = false) {
  const { data } = await api.delete<MetricsConnectionDeleteResponse>(`metrics-connections/${id}`, {
    params: recreateRunners ? { recreateRunners: true } : undefined,
  });
  return data;
}
